import stationDataConfigApi from '@/api/config/stationDataConfig'
import TableFooter from '@/components/common/Table/TableFooter'
import { useAuthContext } from '@/features/auth/context'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import {
  DATA_PAGINATION,
  Emitter,
  Plant,
  SearchStation,
  SetupStation,
} from '@/types'
import { AreaType } from '@/types/areaZone'
import { Historical } from '@/types/historical'
import { HydraulicType } from '@/types/hydraulic'
import { Soil } from '@/types/soil'
import { TabContext, TabPanel, usePagination } from '@mui/lab'
import { Grid, SelectChangeEvent, Tab, Tabs, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import useTranslation from '@/hooks/useTranslation'
import { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import AreaTable from '../components/AreaTable'
import EmitterTable from '../components/EmitterTable'
import EtReferenceTable from '../components/EtReferenceTable'
import HydraulicTable from '../components/HydraulicTable'
import StationTable from '../components/StationTable'
import PlantTableV2 from './PlantTableV2'
import SoilTableV2 from './SoilTableV2'
import classes from './styles.module.scss'

interface CommonDataTableV2Props {
  dataHydraulic?: DATA_PAGINATION<HydraulicType>
  dataEt?: DATA_PAGINATION<Historical>
  dataPlant?: DATA_PAGINATION<Plant>
  dataEmitter?: DATA_PAGINATION<Emitter>
  dataAreas?: AreaType[]
  totalCountAreas?: number
  dataSoil?: DATA_PAGINATION<Soil>
}

const DEFAULT_PAGE_SIZE = 10

const CommonDataTableV2: FC<CommonDataTableV2Props> = ({
  dataHydraulic,
  dataEt,
  dataPlant,
  dataEmitter,
  dataAreas,
  dataSoil,
  totalCountAreas,
}) => {
  const { t } = useTranslation()
  const themeMode = useSelector(selectTheme)

  const [tab, setTab] = useState<string>('0')
  const { interfaceId } = useAuthContext()

  // Pagination state for client-side tabs (0-5)
  const [clientPageSize, setClientPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [clientCurrentPage, setClientCurrentPage] = useState(1)

  // Station tab (6) - server-side pagination
  const [searchParams, setSearchParams] = useState<SearchStation>({
    PageSize: DEFAULT_PAGE_SIZE,
    PageIndex: 1,
    interfaceId: Number(interfaceId),
  })
  const [station, setStation] = useState<DATA_PAGINATION<SetupStation>>()
  const [stationCurrentPage, setStationCurrentPage] = useState(1)

  // Fetch station data
  useEffect(() => {
    const fetchData = async () => {
      const _data = await stationDataConfigApi.getListWithDetail(
        Number(interfaceId),
        searchParams,
      )
      const _dataStations = _data?.data || []
      setStation(_dataStations)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Reset client pagination when tab changes
  const handleChangeTab = (_: any, newTab: string) => {
    setTab(newTab)
    setClientCurrentPage(1)
  }

  // Get data array and total count for current tab
  const getTabData = (tabValue: string) => {
    switch (tabValue) {
      case '0':
        return { data: dataEt?.data || [], totalCount: dataEt?.totalCount || 0 }
      case '1':
        return { data: dataPlant?.data || [], totalCount: dataPlant?.totalCount || 0 }
      case '2':
        return { data: dataSoil?.data || [], totalCount: dataSoil?.totalCount || 0 }
      case '3':
        return { data: dataEmitter?.data || [], totalCount: dataEmitter?.totalCount || 0 }
      case '4':
        return { data: dataAreas || [], totalCount: totalCountAreas || dataAreas?.length || 0 }
      case '5':
        return { data: dataHydraulic?.data || [], totalCount: dataHydraulic?.totalCount || 0 }
      default:
        return { data: [], totalCount: 0 }
    }
  }

  // Client-side pagination helpers
  const clientTabData = useMemo(() => getTabData(tab), [tab, dataEt, dataPlant, dataSoil, dataEmitter, dataAreas, dataHydraulic, totalCountAreas])
  const clientTotalPage = Math.ceil(clientTabData.totalCount / clientPageSize) || 1
  const clientFrom = clientPageSize * (clientCurrentPage - 1) + 1
  const clientTo = Math.min(clientPageSize * clientCurrentPage, clientTabData.totalCount)

  // Paginated data for client-side tabs
  const paginatedData = useMemo(() => {
    const startIndex = (clientCurrentPage - 1) * clientPageSize
    const endIndex = startIndex + clientPageSize
    return clientTabData.data.slice(startIndex, endIndex)
  }, [clientTabData.data, clientCurrentPage, clientPageSize])

  // Client-side pagination handlers
  const handleClientChangeRowsPerPage = (event: SelectChangeEvent) => {
    const newPageSize = parseInt(event.target.value, 10)
    setClientPageSize(newPageSize)
    setClientCurrentPage(1)
  }

  const { items: clientItems } = usePagination({
    count: clientTotalPage,
    page: clientCurrentPage,
    onChange(_event: any, page: any) {
      setClientCurrentPage(page)
    },
  })

  // Station tab handlers (server-side)
  const handleStationChangeRowsPerPage = (event: SelectChangeEvent) => {
    const pageSize = parseInt(event.target.value, 10)
    setStationCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: pageSize,
      PageIndex: 1,
    })
  }

  const { items: stationItems } = usePagination({
    count: station?.totalPage,
    page: stationCurrentPage,
    onChange(_event: any, page: any) {
      setStationCurrentPage(page)
      setSearchParams({
        ...searchParams,
        PageIndex: page,
      })
    },
  })

  // Render data detected message at top
  const renderDataDetected = (totalCount: number) => {
    if (totalCount <= 0) return null
    return (
      <Stack sx={{ padding: '12px 2px' }}>
        <Typography color="#666" fontWeight={500} fontSize={14}>
          {totalCount} rows of data detected
        </Typography>
        <Typography color="#999" fontSize={12}>
          Review the data before proceeding
        </Typography>
      </Stack>
    )
  }

  // Render pagination footer with showing text
  const renderPaginationFooter = (
    totalCount: number,
    totalPage: number,
    rowsPerPage: number,
    handleChangeRowsPerPage: (event: SelectChangeEvent) => void,
    items: any[],
    from: number,
    to: number,
  ) => {
    if (totalCount <= 0) return null
    return (
      <Grid container alignItems="center" justifyContent="space-between" sx={{ padding: '22px 2px' }}>
        <Typography color="#999" lineHeight={1}>
          {t('paginationShowing', { from, to, total: totalCount })}
        </Typography>
        <TableFooter
          totalCount={totalCount}
          totalPage={totalPage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          items={items}
          noPadding
        />
      </Grid>
    )
  }

  const render = (tab: string) => {
    switch (tab) {
      case '0': {
        const paginatedEtData = { ...dataEt, data: paginatedData } as DATA_PAGINATION<Historical>
        return (
          <TabPanel value={'0'} className={classes.tabCommon}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(clientTabData.totalCount)}
              <EtReferenceTable data={paginatedEtData} />
              {renderPaginationFooter(clientTabData.totalCount, clientTotalPage, clientPageSize, handleClientChangeRowsPerPage, clientItems, clientFrom, clientTo)}
            </Stack>
          </TabPanel>
        )
      }
      case '1': {
        const paginatedPlantData = { ...dataPlant, data: paginatedData } as DATA_PAGINATION<Plant>
        return (
          <TabPanel value={'1'} className={classes.tabCommon}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(clientTabData.totalCount)}
              <PlantTableV2 data={paginatedPlantData} />
              {renderPaginationFooter(clientTabData.totalCount, clientTotalPage, clientPageSize, handleClientChangeRowsPerPage, clientItems, clientFrom, clientTo)}
            </Stack>
          </TabPanel>
        )
      }
      case '2': {
        const paginatedSoilData = { ...dataSoil, data: paginatedData } as DATA_PAGINATION<Soil>
        return (
          <TabPanel value={'2'} className={classes.tabCommon}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(clientTabData.totalCount)}
              <SoilTableV2 data={paginatedSoilData} />
              {renderPaginationFooter(clientTabData.totalCount, clientTotalPage, clientPageSize, handleClientChangeRowsPerPage, clientItems, clientFrom, clientTo)}
            </Stack>
          </TabPanel>
        )
      }
      case '3': {
        const paginatedEmitterData = { ...dataEmitter, data: paginatedData } as DATA_PAGINATION<Emitter>
        return (
          <TabPanel value={'3'} className={classes.tabCommon}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(clientTabData.totalCount)}
              <EmitterTable data={paginatedEmitterData} />
              {renderPaginationFooter(clientTabData.totalCount, clientTotalPage, clientPageSize, handleClientChangeRowsPerPage, clientItems, clientFrom, clientTo)}
            </Stack>
          </TabPanel>
        )
      }
      case '4': {
        return (
          <TabPanel value={'4'} className={classes.tabCommon}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(clientTabData.totalCount)}
              <AreaTable data={paginatedData as AreaType[]} totalCount={clientTabData.totalCount} />
              {renderPaginationFooter(clientTabData.totalCount, clientTotalPage, clientPageSize, handleClientChangeRowsPerPage, clientItems, clientFrom, clientTo)}
            </Stack>
          </TabPanel>
        )
      }
      case '5': {
        const paginatedHydraulicData = { ...dataHydraulic, data: paginatedData } as DATA_PAGINATION<HydraulicType>
        return (
          <TabPanel value={'5'} className={classes.tabStation}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(clientTabData.totalCount)}
              <HydraulicTable rows={paginatedHydraulicData} />
              {renderPaginationFooter(clientTabData.totalCount, clientTotalPage, clientPageSize, handleClientChangeRowsPerPage, clientItems, clientFrom, clientTo)}
            </Stack>
          </TabPanel>
        )
      }
      case '6': {
        const rowsPerPage = station?.size ?? DEFAULT_PAGE_SIZE
        const page = station?.page ?? 1
        const totalCount = station?.totalCount ?? 0
        const from = rowsPerPage * (page - 1) + 1
        const to = Math.min(rowsPerPage * page, totalCount)

        return (
          <TabPanel value={'6'} className={classes.tabStation}>
            <Stack flexGrow="1" height="100%">
              {renderDataDetected(totalCount)}
              {station && <StationTable data={station} />}
              {renderPaginationFooter(totalCount, station?.totalPage || 0, rowsPerPage, handleStationChangeRowsPerPage, stationItems, from, to)}
            </Stack>
          </TabPanel>
        )
      }
    }
  }

  return (
    <Stack flexGrow="1">
      <Stack className={classes.wrapper}>
        <TabContext value={tab}>
          <Tabs
            value={tab}
            onChange={handleChangeTab}
            textColor="inherit"
            sx={{
              minHeight: 'unset',
              height: '80px',
              padding: '10px',
              bgcolor: 'background.overrideDefault',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '.MuiTabs-flexContainer': {
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#A5A5A5',
                  borderRadius: '3px',
                },
              },
              '.MuiTab-root': {
                p: 0,
                flex: 1,
                height: '60px',
                minHeight: '40px',
                fontWeight: 700,
                lineHeight: '14px',
                fontSize: '16px',
                color: 'text.main',
                '&.Mui-selected': {
                  color: 'primary.main',
                  borderBottom: {
                    ...(themeMode === mode_type.dark
                      ? { borderBottom: '2px solid #e6eded' }
                      : { borderBottom: '2px solid #004744' }),
                  },
                },
                '&:not(:last-child)': {
                  marginRight: '10px',
                },
              },
              '.MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab value={'0'} label="ET Reference" />
            <Tab value={'1'} label="Plant" />
            <Tab value={'2'} label="Soil" />
            <Tab value={'3'} label="Emitter" />
            <Tab value={'4'} label="Areas & Sub-Areas" />
            <Tab value={'5'} label="Hydraulic" />
            <Tab value={'6'} label="Station" />
          </Tabs>
        </TabContext>
        <TabContext value={tab}>
          <Stack flexGrow={1} bgcolor="background.default">
            {render(tab)}
          </Stack>
        </TabContext>
      </Stack>
    </Stack>
  )
}

export default CommonDataTableV2
